import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Trophy, X, PartyPopper } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export const WinnerBubble = () => {
  const [popup, setPopup] = useState(null);
  const [isExiting, setIsExiting] = useState(false);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/jackpot-announcements`);
      if (res.data && res.data.length > 0) {
        const lastShownId = localStorage.getItem("lastAnnouncementId");
        const latest = res.data[0];
        if (latest && latest.id !== lastShownId) {
          setPopup(latest);
          setIsExiting(false);
          localStorage.setItem("lastAnnouncementId", latest.id);
          // Auto-dismiss after 15 seconds
          setTimeout(() => dismiss(), 15000);
        }
      }
    } catch (e) {
      // silently fail
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
    // Poll every 30 seconds for new announcements
    const interval = setInterval(fetchAnnouncements, 30000);
    // Also react instantly when an admin awards a jackpot in this tab —
    // AdminDashboard dispatches this event so the winner popup appears
    // within a second instead of waiting for the next poll cycle.
    const onAward = () => fetchAnnouncements();
    window.addEventListener("jr:jackpot-awarded", onAward);
    return () => {
      clearInterval(interval);
      window.removeEventListener("jr:jackpot-awarded", onAward);
    };
  }, [fetchAnnouncements]);

  const dismiss = () => {
    setIsExiting(true);
    setTimeout(() => setPopup(null), 400);
  };

  if (!popup) return null;

  return (
    <div
      className={`fixed left-3 sm:left-4 bottom-20 sm:bottom-6 z-[60] transition-all duration-400 ${
        isExiting ? "opacity-0 -translate-x-full" : "opacity-100 translate-x-0 animate-bounce-in"
      }`}
      style={{ maxWidth: "320px" }}
      data-testid="winner-bubble"
    >
      <div className="relative bg-gradient-to-br from-amber-900/95 to-orange-900/95 backdrop-blur-sm rounded-xl p-4 border border-amber-400/40 shadow-2xl shadow-amber-500/30">
        {/* Dismiss button */}
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 text-amber-200/50 hover:text-white transition-colors"
          data-testid="winner-bubble-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Sparkle decoration */}
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center animate-pulse">
          <PartyPopper className="w-3.5 h-3.5 text-amber-900" />
        </div>

        {/* Content */}
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/30">
            <Trophy className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-amber-400/80 uppercase tracking-wider font-bold">
              {(popup.jackpot_tier || "").toUpperCase() === "MEGA"
                ? "GRAND"
                : (popup.jackpot_tier || "").toUpperCase()} Grand Prize
            </p>
            <p className="text-sm font-bold text-white truncate">
              {popup.winner_username}
            </p>
          </div>
        </div>

        <div className="mt-2.5 flex items-center justify-between">
          <div className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500">
            {popup.amount?.toFixed(2)} SC
          </div>
        </div>

        {popup.message && (
          <p className="text-[11px] text-amber-200/60 mt-1.5 leading-tight">
            {popup.message}
          </p>
        )}

        {/* Subtle progress bar for auto-dismiss */}
        <div className="mt-2 h-0.5 bg-amber-800/50 rounded-full overflow-hidden">
          <div
            className="h-full bg-amber-400/60 rounded-full"
            style={{ animation: "shrink 15s linear forwards" }}
          />
        </div>
      </div>

      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
        .animate-bounce-in {
          animation: bounceIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        @keyframes bounceIn {
          0% { opacity: 0; transform: translateX(-100%) scale(0.8); }
          60% { opacity: 1; transform: translateX(5%) scale(1.02); }
          100% { opacity: 1; transform: translateX(0) scale(1); }
        }
      `}</style>
    </div>
  );
};
